/* Duskhaven "Room lights" toggle for the product gallery (2026-10-04, Todd: "a cool idea if implemented well on the
   product pages"). Most Duskhaven sets carry a pair of photos in the same composition: the room lit ("... head on room
   lights on") and only the lamp lit ("... head on room lights off"). When a product has BOTH, this puts a small pill
   (bulb, "Room lights", on/off switch) on the top-right corner of the photo while the visitor is looking at either one,
   and the switch cross-fades to the other photo in place.

   Detection: each slide's <img> alt text plus its file name (dashes and underscores read as spaces) is tested for
   "room lights on" and "room lights off", case-insensitive. Both must exist or nothing is added (inert).
   Behaviour: the switch only ever swaps between the two photos, in place and with no sliding, by jumping the carousel
   to the other slide while a copy of the old photo fades out over it (about 280 ms; instant with reduced motion). The
   carousel's own thumbnails, dots, arrows, swipe and zoom keep working because the real slideshow selects the slide.
   Memory: the last choice is kept in sessionStorage only ("on" is the default). When a shopper reaches the pair by
   swiping or the arrows, it opens on their last choice; picking one of the pair's thumbnails or dots counts as a choice,
   and a variant change or the page's first photo never overrides the variant's own image.
   No JavaScript: both photos stay ordinary slides. Works with the dh-gallery-nav.js arrow/dots fix side by side. */
(() => {
  if (window.__dhRoomLights) return;
  window.__dhRoomLights = true;

  const SEL = '.product-information media-gallery.media-gallery--carousel';
  const PREF_KEY = 'dh-room-lights';
  const HINT_KEY = 'dh-room-lights-hint';
  const FADE_MS = 280;
  const RX_ON = /room lights on\b/;
  const RX_OFF = /room lights off\b/;
  const reducedQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  const reduced = () => !!(reducedQuery && reducedQuery.matches);
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const store = {
    get(k) { try { return window.sessionStorage.getItem(k); } catch (_) { return null; } },
    set(k, v) { try { window.sessionStorage.setItem(k, v); } catch (_) { /* private mode */ } },
  };
  const readPref = () => (store.get(PREF_KEY) === 'off' ? 'off' : 'on');
  const writePref = (v) => store.set(PREF_KEY, v === 'off' ? 'off' : 'on');

  const slidesOf = (ss) => {
    let list = null;
    try { list = ss.slides; } catch (_) { list = null; }
    if (!Array.isArray(list) || !list.length) list = [...ss.querySelectorAll('slideshow-slide')];
    return list;
  };

  const kindOf = (slide) => {
    const img = slide && slide.querySelector('img');
    if (!img) return null;
    const src = img.getAttribute('src') || img.currentSrc || '';
    const file = decodeURIComponent(src.split('?')[0].split('/').pop() || '');
    const text = ((img.getAttribute('alt') || '') + ' ' + file).toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ');
    if (RX_ON.test(text)) return 'on';
    if (RX_OFF.test(text)) return 'off';
    return null;
  };

  // { on: index, off: index } when the gallery has both photos, else null.
  const pairOf = (ss) => {
    const slides = slidesOf(ss);
    let on = -1;
    let off = -1;
    slides.forEach((s, i) => {
      const k = kindOf(s);
      if (k === 'on' && on < 0) on = i;
      if (k === 'off' && off < 0) off = i;
    });
    return on > -1 && off > -1 ? { on, off, slides } : null;
  };

  const BULB = '<svg class="dh-rl__bulb" viewBox="0 0 24 24" aria-hidden="true" focusable="false">'
    + '<path class="dh-rl__glass" d="M12 3.2a6 6 0 0 0-3.7 10.7c.7.6 1.2 1.4 1.2 2.3v.3h5v-.3c0-.9.5-1.7 1.2-2.3A6 6 0 0 0 12 3.2z"/>'
    + '<path class="dh-rl__base" d="M9.8 19.2h4.4M10.6 21.4h2.8"/>'
    + '<path class="dh-rl__rays" d="M12 .6v1M3.6 3.9l.7.7M20.4 3.9l-.7.7M1.2 11.4h1M21.8 11.4h1"/></svg>';

  const controllers = new WeakMap();
  const bound = new WeakSet();

  const setup = (gallery) => {
    const ss = gallery.querySelector('slideshow-component');
    const container = ss && ss.querySelector(':scope > slideshow-container');
    if (!ss || !container) return;
    // Products without both photos get nothing at all (no element, no listeners).
    if (!pairOf(ss)) {
      const prior = controllers.get(ss);
      if (prior && prior.el) { prior.el.remove(); prior.el = null; }
      return;
    }

    let c = controllers.get(ss);
    if (!c) {
      c = { ss, container, el: null, btn: null, hint: null, veil: null, flipping: false, seq: 0, explicitUntil: 0, timer: 0,
        landTimer: 0, hintShown: false, preloaded: false, settled: undefined, lastR: '', lastT: '' };
      controllers.set(ss, c);
    }
    c.container = container;

    // (Re)create the pill if the gallery was re-rendered (a variant change swaps the gallery's markup).
    if (!c.el || !container.contains(c.el)) {
      const el = document.createElement('div');
      el.className = 'dh-rl';
      el.setAttribute('data-visible', 'false');
      el.setAttribute('data-state', 'on');
      el.inert = true;
      el.innerHTML = '<button type="button" class="dh-rl__btn" role="switch" aria-checked="true" aria-label="Room lights">'
        + '<span class="dh-rl__pill">' + BULB + '<span class="dh-rl__label">Room lights</span>'
        + '<span class="dh-rl__track" aria-hidden="true"><span class="dh-rl__knob"></span></span></span></button>'
        + '<span class="dh-rl__hint" aria-hidden="true"></span>';
      container.appendChild(el);
      c.el = el;
      c.btn = el.querySelector('.dh-rl__btn');
      c.hint = el.querySelector('.dh-rl__hint');
      c.btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const pair = pairOf(ss);
        if (!pair) return;
        const next = c.btn.getAttribute('aria-checked') === 'true' ? 'off' : 'on';
        writePref(next);
        hideHint(c);
        paint(c, next);
        flip(c, next, true);
      });
    }

    if (bound.has(ss)) { schedule(c); return; }
    bound.add(ss);

    ss.addEventListener('slideshow:select', (e) => {
      const d = (e && e.detail) || {};
      if (Number.isFinite(Number(d.index))) onSelect(c, d);
      schedule(c);
    });
    ss.addEventListener('scrollend', () => schedule(c));
    const scroller = ss.querySelector('slideshow-slides');
    if (scroller) scroller.addEventListener('scroll', () => schedule(c), { passive: true });
    ss.addEventListener('load', () => schedule(c), true);
    // Thumbnail or dot clicks on one of the pair are an explicit choice, so remember it and don't override it.
    ss.addEventListener('click', (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      const thumb = t.closest('.slideshow-controls__thumbnail');
      const dot = t.closest('.dh-gnav-dot');
      const pick = thumb || dot;
      if (!pick) return;
      const group = [...ss.querySelectorAll(thumb ? '.slideshow-controls__thumbnail' : '.dh-gnav-dot')];
      const idx = group.indexOf(pick);
      const pair = pairOf(ss);
      if (!pair || idx < 0) return;
      c.explicitUntil = performance.now() + 900;
      if (idx === pair.on) writePref('on');
      else if (idx === pair.off) writePref('off');
    }, true);
    if ('ResizeObserver' in window) new ResizeObserver(() => schedule(c)).observe(container);
    window.addEventListener('resize', () => schedule(c));
    new MutationObserver(() => schedule(c)).observe(ss, { subtree: true, attributes: true,
      attributeFilter: ['aria-hidden', 'hidden', 'aria-selected', 'initial-slide', 'transitioning', 'dragging'] });
    update(c);
    setTimeout(() => schedule(c), 400);
    setTimeout(() => schedule(c), 1500);
  };

  const schedule = (c) => { if (!c.timer) c.timer = setTimeout(() => { c.timer = 0; update(c); }, 16); };

  // Put the pill on the top-right corner of the photo itself (tall photos are letterboxed in a wider frame).
  const place = (c, idx) => {
    const slides = slidesOf(c.ss);
    const slide = slides[idx] || slides[0];
    const img = slide && slide.querySelector('img');
    if (!img) return;
    const cr = c.container.getBoundingClientRect();
    const sr = slide.getBoundingClientRect();
    const r = img.getBoundingClientRect();
    if (!cr.width || !cr.height || !sr.width || !r.width || !r.height) return;
    let cx = r.left - sr.left;
    let cy = r.top - sr.top;
    let cw = r.width;
    let ch = r.height;
    if (getComputedStyle(img).objectFit === 'contain' && img.naturalWidth && img.naturalHeight) {
      const ratio = img.naturalWidth / img.naturalHeight;
      if (r.width / r.height > ratio) { cw = r.height * ratio; cx += (r.width - cw) / 2; }
      else { ch = r.width / ratio; cy += (r.height - ch) / 2; }
    }
    const pad = window.matchMedia('(max-width: 749px)').matches ? 10 : 14;
    const frameW = Math.min(cr.width, sr.width);
    const right = Math.min(frameW, cx + cw);
    const vr = Math.max(pad, Math.round(cr.width - right + pad)) + 'px';
    const vt = Math.max(pad, Math.round((sr.top - cr.top) + Math.max(0, cy) + pad)) + 'px';
    if (vr !== c.lastR) { c.container.style.setProperty('--dh-rl-r', vr); c.lastR = vr; }
    if (vt !== c.lastT) { c.container.style.setProperty('--dh-rl-t', vt); c.lastT = vt; }
  };

  const paint = (c, kind) => {
    const on = kind === 'on';
    c.el.setAttribute('data-state', on ? 'on' : 'off');
    c.btn.setAttribute('aria-checked', on ? 'true' : 'false');
  };

  const showHint = (c, kind) => {
    if (c.hintShown || store.get(HINT_KEY) === '1') return;
    c.hintShown = true;
    store.set(HINT_KEY, '1');
    c.hint.textContent = kind === 'on' ? 'Try it: room lights off' : 'Try it: room lights on';
    setTimeout(() => {
      if (c.el.getAttribute('data-visible') === 'true') c.hint.setAttribute('data-show', 'true');
    }, 700);
    setTimeout(() => hideHint(c), 6200);
  };
  const hideHint = (c) => { c.hintShown = true; if (c.hint) c.hint.removeAttribute('data-show'); };

  const load = (img) => new Promise((resolve) => {
    if (!img) { resolve(); return; }
    if (img.loading !== 'eager') img.loading = 'eager';
    const done = () => {
      const d = img.decode ? img.decode().catch(() => {}) : Promise.resolve();
      Promise.race([d, sleep(300)]).then(resolve);
    };
    if (img.complete && img.naturalWidth) { done(); return; }
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', () => resolve(), { once: true });
    setTimeout(resolve, 1200);
  });

  const preload = (c, pair) => {
    if (c.preloaded) return;
    c.preloaded = true;
    [pair.on, pair.off].forEach((i) => {
      const img = pair.slides[i] && pair.slides[i].querySelector('img');
      if (img && img.loading !== 'eager') img.loading = 'eager';
    });
  };

  const update = (c) => {
    const { ss } = c;
    if (!c.el || !ss.isConnected || !c.container.isConnected) return;
    const pair = pairOf(ss);
    let cur = Number(ss.current);
    if (!Number.isFinite(cur)) cur = 0;
    if (!pair) {
      if (c.el.getAttribute('data-visible') !== 'false') { c.el.setAttribute('data-visible', 'false'); c.el.inert = true; }
      return;
    }
    pair.slides.forEach((s, i) => {
      const k = i === pair.on ? 'on' : i === pair.off ? 'off' : '';
      if (k) { if (s.getAttribute('data-dh-room-lights') !== k) s.setAttribute('data-dh-room-lights', k); }
      else if (s.hasAttribute('data-dh-room-lights')) s.removeAttribute('data-dh-room-lights');
    });
    const lo = Math.min(pair.on, pair.off);
    const hi = Math.max(pair.on, pair.off);
    if (cur >= lo - 1 && cur <= hi + 1) preload(c, pair);
    const inPair = cur === pair.on || cur === pair.off;
    if (!ss.hasAttribute('transitioning') && !ss.hasAttribute('dragging')) {
      const was = c.settled;
      c.settled = cur;
      if (!c.flipping && was !== undefined && was !== cur && performance.now() >= c.explicitUntil
        && (was === pair.on || was === pair.off) && inPair) {
        writePref(cur === pair.on ? 'on' : 'off'); // swiped from one photo of the pair to the other
      }
    }
    if (inPair && !c.flipping) {
      const kind = cur === pair.on ? 'on' : 'off';
      paint(c, kind);
      place(c, cur);
    }
    const vis = inPair ? 'true' : 'false';
    if (c.el.getAttribute('data-visible') !== vis) {
      c.el.setAttribute('data-visible', vis);
      c.el.inert = !inPair;
      if (inPair) showHint(c, cur === pair.on ? 'on' : 'off');
      else c.hint.removeAttribute('data-show');
    }
  };

  // The visitor reached a photo of the pair by swiping, the arrows or the dots.
  const onSelect = (c, d) => {
    const pair = pairOf(c.ss);
    if (!pair || !d.userInitiated) return;
    const idx = Number(d.index);
    const prev = Number(d.previousIndex);
    const isPair = (i) => i === pair.on || i === pair.off;
    if (!isPair(idx) || c.flipping) return;
    if (performance.now() < c.explicitUntil) return;
    // Passing through a photo of the pair on the way elsewhere (or swiping between the two) is not a landing;
    // swiping from one to the other is recorded as a choice once the carousel settles (see update).
    if (isPair(prev)) return;
    const want = readPref();
    if ((idx === pair.on ? 'on' : 'off') === want) return;
    if (c.ss.closest('media-gallery') && c.ss.closest('media-gallery').querySelector('dialog[open]')) return;
    clearTimeout(c.landTimer);
    let tries = 0;
    const attempt = () => {
      const p = pairOf(c.ss);
      if (!p || Number(c.ss.current) !== idx) return;
      if ((c.ss.hasAttribute('transitioning') || c.ss.hasAttribute('dragging')) && ++tries < 14) {
        c.landTimer = setTimeout(attempt, 100);
        return;
      }
      flip(c, want, true);
    };
    c.landTimer = setTimeout(attempt, 160);
  };

  const clearVeil = (c) => {
    if (c.veil) { c.veil.remove(); c.veil = null; }
  };

  // Jump the carousel to the other photo of the pair; a copy of the old photo fades out above it.
  const flip = async (c, kind, animate) => {
    const { ss } = c;
    const pair = pairOf(ss);
    if (!pair) return;
    const target = kind === 'on' ? pair.on : pair.off;
    const cur = Number(ss.current);
    if (cur === target) { c.seq++; c.flipping = false; clearVeil(c); paint(c, kind); return; }
    const token = ++c.seq;
    c.flipping = true;
    clearVeil(c);
    const curImg = pair.slides[cur] && pair.slides[cur].querySelector('img');
    const targetImg = pair.slides[target] && pair.slides[target].querySelector('img');
    await load(targetImg);
    if (token !== c.seq) return;
    let veil = null;
    if (animate && !reduced() && curImg && curImg.complete && curImg.naturalWidth) {
      const cr = c.container.getBoundingClientRect();
      const r = curImg.getBoundingClientRect();
      if (r.width && r.height) {
        const cs = getComputedStyle(curImg);
        const slideCs = getComputedStyle(pair.slides[cur]);
        veil = new Image();
        veil.className = 'dh-rl-veil';
        veil.alt = '';
        veil.setAttribute('aria-hidden', 'true');
        veil.decoding = 'sync';
        veil.draggable = false;
        veil.src = curImg.currentSrc || curImg.src;
        veil.style.cssText = `left:${r.left - cr.left}px;top:${r.top - cr.top}px;width:${r.width}px;height:${r.height}px;`
          + `object-fit:${cs.objectFit};object-position:${cs.objectPosition};border-radius:${slideCs.borderRadius};`;
        try { await Promise.race([veil.decode(), sleep(250)]); } catch (_) { /* cached anyway */ }
        if (token !== c.seq) return;
        c.container.appendChild(veil);
        c.veil = veil;
      }
    }
    try { ss.select(target, undefined, { animate: false }); } catch (_) { /* leave the carousel as it is */ }
    paint(c, kind);
    if (veil) {
      void veil.offsetWidth;
      requestAnimationFrame(() => veil.classList.add('is-out'));
      const end = () => { if (c.veil === veil) clearVeil(c); else veil.remove(); };
      veil.addEventListener('transitionend', end, { once: true });
      setTimeout(end, FADE_MS + 160);
    }
    setTimeout(() => {
      if (token === c.seq) { c.flipping = false; schedule(c); }
    }, veil ? FADE_MS : 60);
  };

  const scan = () => document.querySelectorAll(SEL).forEach(setup);
  let t = 0;
  const rescan = () => { clearTimeout(t); t = setTimeout(scan, 80); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
  new MutationObserver(rescan).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', rescan);
})();
